import { toast } from "react-toastify";
import { AdministratorAPI } from "../constants/apiRoutes";
import { userActions } from "../reducers/user";
import apiService from "../utils/configAxios";
import { handleError } from "../utils/handleError";
import { CustomToast } from "../components/Reusable/CustomToast";
import { fetchCartSize } from "./global";
import { t } from "i18next";
import {
  pushDataLayerEvent,
  pushUserData,
  setAnalyticsUserId,
} from "../utils/tracking";

const safelyResetCaptcha = (resetCaptcha) => {
  try {
    resetCaptcha?.();
  } catch (err) {
    console.warn("Unable to reset reCAPTCHA", err);
  }
};

const resolvePostLoginPath = (location = {}) => {
  const searchParams = new URLSearchParams(location.search || "");
  const nextPath = searchParams.get("next");

  if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//")) {
    return nextPath;
  }

  return location.pathname || "/";
};

export const fetchLogin = (
  data,
  hideModal,
  location,
  navigate,
  rememberMe,
  onSuccess
) => {
  return (dispatch) => {
    dispatch(userActions.actionStart());

    return apiService
      .post(AdministratorAPI.login(rememberMe), data)
      .then(async (response) => {
        hideModal();
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.auth.loginSuccess.message")}
          />
        );
        // Očekujemo da BE vrati user objekt (bez tokena)
        dispatch(userActions.setCurrentUser(response.data));
        pushUserData(response.data);
        dispatch(fetchCartSize());
        if (onSuccess) {
          await onSuccess();
          return;
        }
        navigate(resolvePostLoginPath(location), { replace: true });

        // navigate("/dashboard");
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const fetchLoginGoogle = (
  credentialResponse,
  hideModal,
  navigate,
  location,
  rememberMe,
  onSuccess
) => {
  return (dispatch) => {
    dispatch(userActions.actionStart());

    return apiService
      .post(AdministratorAPI.loginGoolge(rememberMe), credentialResponse)
      .then(async (response) => {
        hideModal();
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.auth.loginSuccess.message")}
          />
        );
        // Očekujemo da BE vrati user objekt (bez tokena)
        dispatch(userActions.setCurrentUser(response.data));
        pushUserData(response.data);
        dispatch(fetchCartSize());
        if (onSuccess) {
          await onSuccess();
          return;
        }
        navigate(resolvePostLoginPath(location), { replace: true });

        // navigate("/dashboard");
      })
      .catch((err) => {
        const googleLoginError = [
          err?.response?.data?.name,
          err?.response?.data?.message,
          err?.message,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (
          err?.response?.status === 404 ||
          googleLoginError.includes("adresa") ||
          googleLoginError.includes("address") ||
          googleLoginError.includes("not found")
        ) {
          toast(
            <CustomToast
              type="error"
              title={t("error.error")}
              message={t("error.googleAccountNotFound")}
            />
          );
          return;
        }

        handleError(err);
      });
  };
};

export const fetchLogout = () => {
  return (dispatch) => {
    dispatch(userActions.actionStart());

    return apiService
      .post(AdministratorAPI.logout())
      .then((response) => {
        setAnalyticsUserId(null);
        dispatch(userActions.clearUser());
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const fetchRegistration = (data, hideModal, resetCaptcha) => {
  return (dispatch) => {
    dispatch(userActions.actionStart());

    return apiService
      .post(AdministratorAPI.register(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.auth.registrationSuccess.message")}
          />
        );
        hideModal();
        safelyResetCaptcha(resetCaptcha);
        pushDataLayerEvent({
          event: "sign_up",
          user_id: response.data?.id ? String(response.data.id) : undefined,
        });
      })
      .catch((err) => {
        safelyResetCaptcha(resetCaptcha);
        handleError(err);
      });
  };
};

export const fetchRegistrationGoogle = (data, hideModal) => {
  return (dispatch) => {
    dispatch(userActions.actionStart());

    return apiService
      .post(AdministratorAPI.registerGoogle(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.auth.registrationSuccess.message2")}
          />
        );
        hideModal();
        pushDataLayerEvent({
          event: "sign_up",
          user_id: response.data?.id ? String(response.data.id) : undefined,
        });
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const checkAuth = () => {
  return (dispatch) => {
    return apiService
      .get(AdministratorAPI.me())
      .then((response) => {
        dispatch(userActions.setCurrentUser(response.data));
        pushUserData(response.data, null);
      })
      .catch((err) => {
        dispatch(userActions.clearUser()); // ← OVO JE KLJUČ
      });
  };
};

export const changeProfileData = (data) => {
  return (dispatch) => {
    return apiService
      .put(AdministratorAPI.changeData(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.profile.changeDataSuccess.message")}
          />
        );
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const changePassword = (data, handleResetPasswordData) => {
  return (dispatch) => {
    return apiService
      .post(AdministratorAPI.changePassword(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.profile.changePasswordSuccess.message")}
          />
        );
        handleResetPasswordData();
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const resetPassword = (data) => {
  return (dispatch) => {
    return apiService
      .post(AdministratorAPI.resetPassword(), data)
      .then((response) => {
        // dispatch(userActions.setCurrentUser(response.data));
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.resetPassword.requestSuccess.message")}
          />
        );
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const resetPasswordConfirm = (data, navigate) => {
  return (dispatch) => {
    return apiService
      .post(AdministratorAPI.resetPasswordConfirm(), data)
      .then((response) => {
        // dispatch(userActions.setCurrentUser(response.data));
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.resetPassword.confirmSuccess.message")}
          />
        );
        navigate("/");
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const verifyAccount = (data, navigate) => {
  return (dispatch) => {
    return apiService
      .post(AdministratorAPI.verification(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.verification.verifySuccess.message")}
          />
        );
        setTimeout(() => {
          navigate("/?login=true");
        }, 1000);
      })
      .catch((err) => {
        navigate("/");
        handleError(err);
      });
  };
};

export const resendVerification = (
  data,
  handleVerificationModal,
  hideModal
) => {
  return (dispatch) => {
    return apiService
      .post(AdministratorAPI.resendVerification(), data)
      .then((response) => {
        handleVerificationModal();
        hideModal();
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.verification.resendVerification.message")}
          />
        );
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const fetchUserInfo = () => {
  return (dispatch) => {
    return apiService
      .get(AdministratorAPI.info())
      .then((response) => {
        dispatch(userActions.setCurrentUser(response.data));
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const deactivateAccount = (navigate) => {
  return (dispatch) => {
    return apiService
      .put(AdministratorAPI.deactivateAccount())
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("success.success")}
            message={t("success.account.deactivateSuccess.message")}
          />
        );
        dispatch(userActions.clearUser());

        setTimeout(() => {
          navigate("/");
        }, 1000);
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const fetchChangeNewsletter = (data, setNewsletterDialogOpen) => {
  return (dispatch) => {
    return apiService
      .put(AdministratorAPI.changeNewsletter(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("profile.user.newsletterData.unsubscribeSuccessTitle")}
            message={t("profile.user.newsletterData.unsubscribeSuccessMessage")}
          />
        );
        setNewsletterDialogOpen(false);
      })
      .catch((err) => {
        handleError(err);
      });
  };
};

export const fetchChangeNewsletterAccept = (data) => {
  return (dispatch) => {
    return apiService
      .put(AdministratorAPI.changeNewsletter(), data)
      .then((response) => {
        toast(
          <CustomToast
            type="success"
            title={t("profile.user.newsletterData.subscribeSuccessTitle")}
            message={t("profile.user.newsletterData.subscribeSuccessMessage")}
          />
        );
      })
      .catch((err) => {
        handleError(err);
      });
  };
};
